import type { ImportFileType, ImportValidationResult, ImportValidationError, ImportValidationWarning } from '../types';
import { getRequiredColumns } from './columnMappingConfig';
import { parseWktPolygonRing, toHHMM } from './fileParsers';

export function validateImportFile(
  fileType: ImportFileType,
  rows: Record<string, unknown>[],
  columnMapping: Record<string, string>
): ImportValidationResult {
  const errors: ImportValidationError[] = [];
  const warnings: ImportValidationWarning[] = [];
  let validRowCount = 0;
  const invalidRowIndexes: number[] = [];

  const requiredKeys = getRequiredColumns(fileType);
  const missingMappings = requiredKeys.filter(key => !columnMapping[key]);

  if (missingMappings.length > 0) {
    missingMappings.forEach(key => {
      errors.push({
        row: 0,
        column: key,
        message: `Required column "${key}" is not mapped.`,
      });
    });
    return { isValid: false, errors, warnings, validRowCount: 0, totalRowCount: rows.length, invalidRowIndexes: rows.map((_, i) => i) };
  }

  rows.forEach((row, rowIndex) => {
    const rowNum = rowIndex + 2; // 1-indexed, +1 for header
    let rowValid = true;

    if (fileType === 'rides') {
      const latKeys = ['originLat', 'destLat'];
      const lngKeys = ['originLng', 'destLng'];

      for (const key of latKeys) {
        const col = columnMapping[key];
        if (!col) continue;
        const val = parseFloat(String(row[col] ?? ''));
        if (isNaN(val)) {
          errors.push({ row: rowNum, column: col, message: `Invalid latitude value.` });
          rowValid = false;
        } else if (val < -90 || val > 90) {
          errors.push({ row: rowNum, column: col, message: `Latitude ${val} out of range (-90 to 90).` });
          rowValid = false;
        }
      }

      for (const key of lngKeys) {
        const col = columnMapping[key];
        if (!col) continue;
        const val = parseFloat(String(row[col] ?? ''));
        if (isNaN(val)) {
          errors.push({ row: rowNum, column: col, message: `Invalid longitude value.` });
          rowValid = false;
        } else if (val < -180 || val > 180) {
          errors.push({ row: rowNum, column: col, message: `Longitude ${val} out of range (-180 to 180).` });
          rowValid = false;
        }
      }

      const timeCol = columnMapping['requestTime'];
      if (timeCol) {
        const timeVal = row[timeCol];
        if (timeVal === undefined || timeVal === null || timeVal === '') {
          warnings.push({ row: rowNum, column: timeCol, message: `Missing request time, will use default.` });
        }
      }
    }

    if (fileType === 'points') {
      const latCol = columnMapping['latitude'];
      const lngCol = columnMapping['longitude'];

      if (latCol) {
        const val = parseFloat(String(row[latCol] ?? ''));
        if (isNaN(val)) {
          errors.push({ row: rowNum, column: latCol, message: `Invalid latitude value.` });
          rowValid = false;
        } else if (val < -90 || val > 90) {
          errors.push({ row: rowNum, column: latCol, message: `Latitude ${val} out of range (-90 to 90).` });
          rowValid = false;
        }
      }

      if (lngCol) {
        const val = parseFloat(String(row[lngCol] ?? ''));
        if (isNaN(val)) {
          errors.push({ row: rowNum, column: lngCol, message: `Invalid longitude value.` });
          rowValid = false;
        } else if (val < -180 || val > 180) {
          errors.push({ row: rowNum, column: lngCol, message: `Longitude ${val} out of range (-180 to 180).` });
          rowValid = false;
        }
      }

      const nameCol = columnMapping['name'];
      if (nameCol && (!row[nameCol] || String(row[nameCol]).trim() === '')) {
        warnings.push({ row: rowNum, column: nameCol, message: `Missing name, will be auto-generated.` });
      }
    }

    if (fileType === 'polygons') {
      const wktCol = columnMapping['wkt'];
      if (wktCol && !parseWktPolygonRing(String(row[wktCol] ?? ''))) {
        errors.push({ row: rowNum, column: wktCol, message: `Not a valid POLYGON/MULTIPOLYGON WKT.` });
        rowValid = false;
      }
    }

    if (fileType === 'fleet_shifts') {
      const startCol = columnMapping['shiftStart'];
      const endCol = columnMapping['shiftEnd'];
      const seatsCol = columnMapping['standardSeats'];

      if (startCol && !toHHMM(row[startCol])) {
        errors.push({ row: rowNum, column: startCol, message: `Missing or invalid shift start time.` });
        rowValid = false;
      }
      if (endCol && !toHHMM(row[endCol])) {
        errors.push({ row: rowNum, column: endCol, message: `Missing or invalid shift end time.` });
        rowValid = false;
      }
      for (const key of ['startLat', 'startLng', 'endLat', 'endLng']) {
        const col = columnMapping[key];
        const val = parseFloat(String(row[col] ?? ''));
        const limit = key.endsWith('Lat') ? 90 : 180;
        if (col && !(Math.abs(val) <= limit)) {
          errors.push({ row: rowNum, column: col, message: `Invalid ${key.endsWith('Lat') ? 'latitude' : 'longitude'}.` });
          rowValid = false;
        }
      }
      if (seatsCol) {
        const seats = parseInt(String(row[seatsCol] ?? ''), 10);
        if (isNaN(seats) || seats < 1) {
          errors.push({ row: rowNum, column: seatsCol, message: `Standard seats must be at least 1.` });
          rowValid = false;
        }
      }
    }

    if (rowValid) validRowCount++;
    else invalidRowIndexes.push(rowIndex);
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    validRowCount,
    totalRowCount: rows.length,
    invalidRowIndexes,
  };
}
